import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import Edgemarc from '../tabs/edgemarc/Edgemarc.jsx';
import Dispatch from '../tabs/Dispatch.jsx';
import FieldEngineer from '../tabs/FieldEngineer.jsx';
import Folder from '../tabs/Folder.jsx';
import Email from '../tabs/Email.jsx';
import Fax from '../tabs/Fax.jsx';
import Whois from '../tabs/Whois.jsx';
import Tabs from '../tabs/Tabs.jsx';
import TicketData from '../../components/ui/TicketData.jsx';
import TabEditModal from '../../components/ui/TabEditModal.jsx';
import { tabService } from '../../services';
import './MyTicketsHump.css';

function MyTicketsHump() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [customTabNames, setCustomTabNames] = useState({});
  const [customTabColors, setCustomTabColors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTabId, setEditingTabId] = useState(null);

  // Convert Tailwind color class to hex value
  const getColorHex = (bgColorClass) => {
    const colorMap = {
      'bg-blue-300': '#93c5fd',
      'bg-purple-300': '#d8b4fe',
      'bg-red-300': '#fca5a5',
      'bg-pink-300': '#f9a8d4',
      'bg-yellow-300': '#fde047',
      'bg-green-300': '#86efac',
      'bg-orange-300': '#fdba74',
      'bg-[#CCDFA9]': '#CCDFA9',
      'bg-[#FFD4A3]': '#FFD4A3',
    };
    return colorMap[bgColorClass] || '#93c5fd';
  };

  // Define tab data based on whether we have an ID
  const tabData = id ? [
    { id: 0, label: 'Quick copy', color: '#93c5fd', component: Email, bgcolor: 'bg-blue-300', nocolor: 'bg-blue-100', hovercolor: 'hover:bg-blue-200' },
    { id: 1, label: 'Folder', color: '#d8b4fe', component: Folder, bgcolor: 'bg-purple-300', nocolor: 'bg-purple-100', hovercolor: 'hover:bg-purple-200' },
    { id: 2, label: 'Edgemarc', color: '#fca5a5', component: Edgemarc, bgcolor: 'bg-red-300', nocolor: 'bg-red-100', hovercolor: 'hover:bg-red-200' },
    { id: 3, label: 'eFax', color: '#f9a8d4', component: Fax, bgcolor: 'bg-pink-300', nocolor: 'bg-pink-100', hovercolor: 'hover:bg-pink-200' },
    { id: 4, label: 'Whois', color: '#fde047', component: Whois, bgcolor: 'bg-yellow-300', nocolor: 'bg-yellow-100', hovercolor: 'hover:bg-yellow-200' },
    { id: 5, label: 'Dispatch', color: '#CCDFA9', component: Dispatch, bgcolor: 'bg-[#CCDFA9]', nocolor: 'bg-green-100', hovercolor: 'hover:bg-green-200' },
    { id: 6, label: 'Field Engineer', color: '#FFD4A3', component: FieldEngineer, bgcolor: 'bg-[#FFD4A3]', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200' },
    { id: 7, label: 'Tab 1', color: '#fdba74', component: Tabs, tabId: 7, bgcolor: 'bg-orange-300', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200', isCustomizable: true },
    { id: 8, label: 'Tab 2', color: '#fdba74', component: Tabs, tabId: 8, bgcolor: 'bg-orange-300', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200', isCustomizable: true },
    { id: 9, label: 'Tab 3', color: '#fdba74', component: Tabs, tabId: 9, bgcolor: 'bg-orange-300', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200', isCustomizable: true },
  ] : [
    { id: 0, label: 'Tab 1', color: '#f28b82' },
    { id: 1, label: 'Tab 2', color: '#fbbc04' },
    { id: 2, label: 'Tab 3', color: '#fff475' },
    { id: 3, label: 'Tab 4', color: '#8ab4f8' },
    { id: 4, label: 'Tab 5', color: '#a7ffeb' },
    { id: 5, label: 'Tab 6', color: '#d7aefb' },
    { id: 6, label: 'Tab 7', color: '#81c995' },
  ];

  // Fetch custom tab data from database
  useEffect(() => {
    const fetchCustomTabData = async () => {
      if (!id) return;

      try {
        const data = await tabService.getCustomTabNames(id);
        const tabNamesMap = {};
        const tabColorsMap = {};

        data.forEach(item => {
          tabNamesMap[item.tab_number] = item.custom_name;

          if (item.bgcolor && item.nocolor && item.hovercolor) {
            tabColorsMap[item.tab_number] = {
              bgcolor: item.bgcolor,
              nocolor: item.nocolor,
              hovercolor: item.hovercolor
            };
          }
        });

        setCustomTabNames(tabNamesMap);
        setCustomTabColors(tabColorsMap);
      } catch (error) {
        console.error('Error fetching custom tab data:', error);
      }
    };
    fetchCustomTabData();
  }, [id]);

  // Get tab title (custom or default)
  const getTabTitle = (tabId, defaultLabel) => {
    // For tabs 7, 8, 9 (Tab 1, Tab 2, Tab 3), use tab numbers 1, 2, 3
    if (tabId >= 7 && tabId <= 9) {
      const tabNumber = tabId - 6; // 7->1, 8->2, 9->3
      return customTabNames[tabNumber] || defaultLabel;
    }
    return defaultLabel;
  };

  // Get tab color (custom or default)
  const getTabColor = (tabId, defaultColor, defaultBgcolor) => {
    if (tabId >= 7 && tabId <= 9) {
      const tabNumber = tabId - 6; // 7->1, 8->2, 9->3
      const customColors = customTabColors[tabNumber];

      if (customColors && customColors.bgcolor) {
        return getColorHex(customColors.bgcolor);
      }
    }
    return defaultColor;
  };

  // Get tab colors object for Tabs component
  const getTabColors = (tabId) => {
    const tab = tabData.find(t => t.id === tabId);
    if (!tab) return { bgcolor: 'bg-orange-300', nocolor: 'bg-orange-100', hovercolor: 'hover:bg-orange-200' };

    if (tabId >= 7 && tabId <= 9) {
      const tabNumber = tabId - 6;
      const customColors = customTabColors[tabNumber];
      if (customColors) {
        return customColors;
      }
    }

    return {
      bgcolor: tab.bgcolor,
      nocolor: tab.nocolor,
      hovercolor: tab.hovercolor
    };
  };

  // Handle edit button click
  const handleEditClick = (tabId) => {
    if (tabId >= 7 && tabId <= 9) {
      setEditingTabId(tabId);
      setIsModalOpen(true);
    }
  };

  // Handle modal save
  const handleModalSave = async (name, colorData) => {
    if (editingTabId && editingTabId >= 7 && editingTabId <= 9 && id) {
      const tabNumber = editingTabId - 6; // 7->1, 8->2, 9->3

      try {
        await tabService.updateCustomTabName(
          id,
          tabNumber,
          name,
          {
            bgcolor: colorData.bgcolor,
            nocolor: colorData.nocolor,
            hovercolor: colorData.hovercolor
          }
        );

        setCustomTabNames(prev => ({
          ...prev,
          [tabNumber]: name
        }));

        setCustomTabColors(prev => ({
          ...prev,
          [tabNumber]: {
            bgcolor: colorData.bgcolor,
            nocolor: colorData.nocolor,
            hovercolor: colorData.hovercolor
          }
        }));
      } catch (error) {
        console.error('Error updating tab:', error);
      }
    }
  };

  // Update CSS custom property for active tab color
  useEffect(() => {
    const activeTabData = tabData[activeTab];
    if (!activeTabData) return;

    const activeColor = getTabColor(activeTabData.id, activeTabData.color, activeTabData.bgcolor);
    document.documentElement.style.setProperty('--active-color', activeColor);
  }, [activeTab, tabData, customTabColors]);

  // If there's a ticket ID, show the ticket view with hump tabs and real content
  if (id) {
    const activeTabData = tabData[activeTab];
    const ActiveComponent = activeTabData.component;

    return (
      <>
        <TicketData />
        <div className="hump-tab-bar">
          <div className="hump-tab-strip">
            {tabData.map((tab) => {
              const displayLabel = getTabTitle(tab.id, tab.label);
              const tabColor = getTabColor(tab.id, tab.color, tab.bgcolor);
              const isCustomizable = tab.isCustomizable;

              return (
                <div
                  key={tab.id}
                  className={`hump-tab ${activeTab === tab.id ? 'hump-tab-active' : ''}`}
                  data-label={displayLabel}
                  style={{ '--tab-color': tabColor }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span>{displayLabel}</span>
                  {isCustomizable && activeTab === tab.id && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="hump-tab-edit-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(tab.id);
                      }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                      />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hump-tab-content">
            {activeTabData.tabId !== undefined ? (
              <ActiveComponent tabId={activeTabData.tabId} tabColors={getTabColors(activeTabData.id)} />
            ) : (
              <ActiveComponent />
            )}
          </div>
        </div>

        {/* Tab Edit Modal */}
        {isModalOpen && editingTabId && (
          <TabEditModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingTabId(null);
            }}
            tabName={getTabTitle(editingTabId, '')}
            tabColor={getTabColors(editingTabId)}
            onSave={handleModalSave}
          />
        )}
      </>
    );
  }

  // Otherwise show the demo hump tabs
  return (
    <div className="hump-tab-bar">
      <div className="hump-tab-strip">
        {tabData.map((tab) => (
          <div
            key={tab.id}
            className={`hump-tab ${activeTab === tab.id ? 'hump-tab-active' : ''}`}
            data-label={tab.label}
            style={{ '--tab-color': tab.color }}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className="hump-tab-content">
        <p>Content for {tabData[activeTab].label}</p>
        <p className="mt-4">
          This is a demonstration of the hump tab design. Click on different tabs
          to see the color change dynamically!
        </p>
      </div>
    </div>
  );
}

export default MyTicketsHump;
